import { useEffect, useState } from 'react';

import {
  ModuleNameEnum,
  useCreateAddOnTaskLazyQuery,
  GetEncounterTaskByEncounterTaskIdQuery,
} from 'generated/graphql';

import { Logger } from 'utils';
import { Tokens } from 'config';
import { useToastMessage } from 'hooks';
import UI from 'ui';
import * as Options from './enumsToOptions';
import { TModalState, TSetModalState } from './types';

const ClinicianOrderModal = ({
  encounter,
  setModalState,
  modalState,
}: {
  encounter?: NonNullable<
    GetEncounterTaskByEncounterTaskIdQuery['encounterTask']
  >['encounter'];
  setModalState: TSetModalState;
  modalState: TModalState;
}): JSX.Element => {
  const [createAddOnTask, { data, error }] = useCreateAddOnTaskLazyQuery();
  const { setToastMessage } = useToastMessage();

  const [moduleName, setModuleName] = useState(ModuleNameEnum.DEFAULT);

  const clinicianOrderModalContent = (
    <>
      <p
        style={{
          marginBottom: `calc(${Tokens.rhythm} * 2)`,
          marginTop: '0px',
        }}
      >
        Add the module that the clinician is ordering. This module will be added
        to the{' '}
        <strong>
          {encounter?.serviceLine?.name} - {encounter?.serviceLine?.subType}
        </strong>{' '}
        encounter.
      </p>
      <UI.Select
        id="module"
        label={'Which module is the clinician ordering?'}
        name="module"
        options={Options.ClinicianOrderModuleOptions}
        onChange={(selectedOption) => {
          setModuleName(
            (selectedOption && selectedOption[0].value) as ModuleNameEnum,
          );
        }}
      />
    </>
  );

  useEffect(() => {
    if (data) {
      setToastMessage(
        `Successfully added 1 module`,
        UI.Toast.SeverityLevel.Success,
      );
      setModalState({
        selectedType: null,
        isOpen: false,
      });
    }
  }, [data]);

  useEffect(() => {
    if (error) {
      Logger.error(error);
      setToastMessage(`Failed to add task`, UI.Toast.SeverityLevel.Error);
    }
  }, [error]);

  return (
    <UI.Modal
      body={clinicianOrderModalContent}
      closeButtonLabel="Cancel"
      onClose={() => {
        setModalState({
          selectedType: null,
          isOpen: false,
        });
      }}
      title={'Add clinician order'}
      size="small"
      open={modalState.isOpen}
      footerButtons={[
        {
          name: 'addClinicianOrderButton',
          children: 'Add Module',
          variant: 'primary',
          size: 'small',
          onClick: () => {
            if (moduleName !== ModuleNameEnum.DEFAULT) {
              createAddOnTask({
                variables: {
                  id: encounter?.encounterId as number,
                  moduleName,
                },
              });
            }
          },
        },
      ]}
    />
  );
};

export default ClinicianOrderModal;
